// Module 6 — Treaties & Facultative.
// Route: /reinsurance/treaties
//
// Built to the approved design (2026-09-16): programs, layers and market
// participation, with the record opening in a drawer rather than on its own
// page.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModulePage from './ModulePage';
import TreatyDrawer from './TreatyDrawer';
import {
  C, Icons, Kpi, Pill, PrimaryButton, GhostButton, moneyMixed, date, humanise,
} from './kit';

const STRUCTURE_LABELS = {
  quota_share: 'Quota Share', surplus: 'Surplus Share',
  excess_of_loss: 'Excess of Loss', stop_loss: 'Stop Loss', facultative: 'Per Risk',
};

// "USD 10M / 100M" for a layered programme, "20% share" for a proportional
// one — the ATTACHMENT / LIMIT column says different things per structure and
// pretending otherwise would print "20% / undefined".
function attachmentLimit(t) {
  const l = t.limits || {};
  if (t.type === 'quota_share' || t.type === 'facultative') {
    return l.cessionPercent != null ? `${l.cessionPercent}% share` : '—';
  }
  if (t.type === 'surplus') {
    return l.lines != null ? `${l.lines} lines` : (l.cessionPercent != null ? `${l.cessionPercent}% share` : '—');
  }
  if (l.retention != null && l.limit != null) {
    const m = (n) => `${Math.round(n / 1e6)}M`;
    return `${t.currency || ''} ${m(l.retention)} / ${m(l.limit)}`.trim();
  }
  return '—';
}

// The create form. `kind` is fixed by which button was pressed, so it is not
// a field: a form that asks you to re-state what you just clicked is a form
// that lets you contradict yourself.
const treatyForm = (kind, t) => ({
  title: kind === 'facultative'
    ? t('screens.reTreaties.newFacTitle', 'New facultative risk')
    : t('screens.reTreaties.newTreatyTitle', 'New treaty'),
  subtitle: kind === 'facultative'
    ? t('screens.reTreaties.newFacSub', 'A single risk placed on its own terms')
    : t('screens.reTreaties.newTreatySub', 'A programme covering a class of business'),
  submitLabel: t('screens.reTreaties.create', 'Create'),
  endpoint: '/reinsurance/treaties',
  fields: [
    { key: 'name', label: t('screens.reTreaties.fName', 'Programme name'), required: true,
      placeholder: kind === 'facultative' ? 'Marine Facultative — Al Noor fleet' : 'Property CAT Layer 2027' },
    { key: 'cedantName', label: t('screens.reTreaties.fCedant', 'Cedant'), required: true,
      placeholder: 'Riyadh Properties Co.' },
    { key: 'type', label: t('screens.reTreaties.fStructure', 'Structure'), type: 'select', required: true,
      defaultValue: kind === 'facultative' ? 'facultative' : 'quota_share',
      options: [
        { value: 'quota_share', label: 'Quota Share' },
        { value: 'surplus', label: 'Surplus' },
        { value: 'excess_of_loss', label: 'Excess of Loss' },
        { value: 'stop_loss', label: 'Stop Loss' },
        { value: 'facultative', label: 'Facultative' },
      ] },
    { key: 'classOfBusiness', label: t('screens.reTreaties.fClass', 'Line of business'), required: true,
      placeholder: 'Property' },
    { key: 'territory', label: t('screens.reTreaties.fTerritory', 'Territory'), placeholder: 'KSA, GCC' },
    { key: 'effectiveDate', label: t('screens.reTreaties.fInception', 'Inception'), type: 'date', required: true },
    { key: 'expiryDate', label: t('screens.reTreaties.fExpiry', 'Expiry'), type: 'date' },
    { key: 'currency', label: t('screens.reTreaties.fCurrency', 'Currency'), defaultValue: 'USD',
      help: t('screens.reTreaties.fCurrencyHelp', 'ISO code. Amounts on this programme are never summed with another currency.') },
    { key: 'premiumEstimated', label: t('screens.reTreaties.fPremium', 'Estimated premium'), type: 'number',
      placeholder: '18500000' },
  ],
  validate: (v) => {
    // A programme cannot expire before it starts. The server would store it
    // quite happily and every duration on the screen would go negative.
    if (v.effectiveDate && v.expiryDate && v.expiryDate < v.effectiveDate) {
      return { expiryDate: 'Expiry cannot be before inception' };
    }
    return null;
  },
  toPayload: (v) => ({
    name: v.name.trim(),
    kind,
    type: v.type,
    // partyRefSchema carries the id AND the name; a treaty typed in by hand
    // has no counterparty record yet, and the name is what a bound contract
    // is written against.
    cedant: { name: v.cedantName.trim() },
    classOfBusiness: v.classOfBusiness.trim(),
    ...(v.territory ? { territory: v.territory.trim() } : {}),
    effectiveDate: v.effectiveDate,
    ...(v.expiryDate ? { expiryDate: v.expiryDate } : {}),
    currency: (v.currency || 'USD').trim().toUpperCase(),
    ...(v.premiumEstimated !== '' && v.premiumEstimated !== undefined
      ? { premium: { estimated: Number(v.premiumEstimated) } }
      : {}),
    // Everything starts as a draft. A record that appeared already Active
    // would be claiming cover nobody has bound.
    status: 'draft',
  }),
});

export default function TreatiesList() {
  const { t } = useTranslation();
  const [formKind, setFormKind] = useState('treaty');

  const columns = [
    { key: 'ref', label: t('screens.reTreaties.colNumber', 'Ref'), width: 132,
      render: (r) => <span style={{ fontWeight: 600, color: C.ink }}>{r.treatyNumber}</span> },
    { key: 'cedant', label: t('screens.reTreaties.colCedant', 'Cedant'),
      render: (r) => r.cedant?.name || '—' },
    { key: 'kind', label: t('screens.reTreaties.colProgramType', 'Program type'), width: 118,
      render: (r) => <Pill status={r.kind === 'facultative' ? 'quoted' : 'in_review'}
        label={r.kind === 'facultative' ? 'Facultative' : 'Treaty'} size='sm' /> },
    { key: 'classOfBusiness', label: t('screens.reTreaties.colLob', 'Line of business'),
      render: (r) => r.classOfBusiness || '—' },
    { key: 'type', label: t('screens.reTreaties.colStructure', 'Structure'),
      render: (r) => STRUCTURE_LABELS[r.type] || humanise(r.type) },
    { key: 'attachment', label: t('screens.reTreaties.colAttachment', 'Attachment / limit'),
      render: attachmentLimit },
    { key: 'inception', label: t('screens.reTreaties.colInception', 'Inception'), width: 108,
      render: (r) => date(r.effectiveDate) },
    { key: 'markets', label: t('screens.reTreaties.colMarkets', 'Markets'),
      render: (r) => {
        const m = r.marketNames?.length ? r.marketNames : (r.shares || []).map(s => s.name).filter(Boolean);
        if (!m.length) return <span style={{ color: C.faint }}>—</span>;
        return (
          <span style={{ fontSize: 12.5 }}>
            {m.slice(0, 2).join(', ')}{m.length > 2 ? <span style={{ color: C.muted }}> +{m.length - 2}</span> : null}
          </span>
        );
      } },
    { key: 'status', label: t('screens.reTreaties.colStatus', 'Status'), width: 100,
      render: (r) => <Pill status={r.status} /> },
  ];

  return (
    <ModulePage
      title={t('screens.reTreaties.title', 'Treaties & Facultative')}
      subtitle={t('screens.reTreaties.subtitle', 'Programs, layers and market participation')}
      endpoint='/reinsurance/treaties'
      searchPlaceholder={t('screens.reTreaties.search', 'Search treaties, cedants, reference…')}
      createForm={treatyForm(formKind, t)}
      actions={({ openCreate }) => (
        <>
          <GhostButton icon={Icons.clip()}>{t('screens.reTreaties.import', 'Import')}</GhostButton>
          <PrimaryButton onClick={() => { setFormKind('treaty'); openCreate(); }}>
            {t('screens.reTreaties.new', 'New treaty')}
          </PrimaryButton>
          <PrimaryButton onClick={() => { setFormKind('facultative'); openCreate(); }}>
            {t('screens.reTreaties.newFac', 'New facultative risk')}
          </PrimaryButton>
        </>
      )}
      renderKpis={(s) => (
        <>
          <Kpi icon={Icons.doc()} tone='blue'
            value={s?.liveTreaties?.value ?? '—'} trend={s?.liveTreaties?.trend}
            comparison={s?.liveTreaties?.comparison}
            label={t('screens.reTreaties.kpiLive', 'Live treaties')} />
          <Kpi icon={Icons.shield()} tone='green'
            value={s?.facultativeRisks?.value ?? '—'} trend={s?.facultativeRisks?.trend}
            comparison={s?.facultativeRisks?.comparison}
            label={t('screens.reTreaties.kpiFac', 'Facultative risks')} />
          <Kpi icon={Icons.clock()} tone='amber'
            value={s?.expiringPrograms?.value ?? '—'} trend={s?.expiringPrograms?.trend}
            comparison={s?.expiringPrograms?.comparison}
            label={t('screens.reTreaties.kpiExpiring', 'Expiring programs')} />
          <Kpi icon={Icons.users()} tone='neutral'
            value={moneyMixed(s?.totalSignedLines)} trend={s?.totalSignedLines?.trend}
            comparison={s?.totalSignedLines?.comparison}
            label={t('screens.reTreaties.kpiSigned', 'Total signed lines')} />
        </>
      )}
      filters={[
        { key: 'kind', label: 'Program type', options: [
          { value: 'all', label: 'All program types' },
          { value: 'treaty', label: 'Treaty' },
          { value: 'facultative', label: 'Facultative' },
        ] },
        { key: 'type', label: 'Structure', options: [
          { value: 'all', label: 'All structures' },
          { value: 'quota_share', label: 'Quota share' },
          { value: 'surplus', label: 'Surplus' },
          { value: 'excess_of_loss', label: 'Excess of loss' },
          { value: 'stop_loss', label: 'Stop loss' },
          { value: 'facultative', label: 'Facultative' },
        ] },
        { key: 'status', label: 'Status', options: [
          { value: 'all', label: 'All statuses' },
          { value: 'draft', label: 'Draft' },
          { value: 'pending', label: 'Pending' },
          { value: 'quoted', label: 'Quoted' },
          { value: 'bound', label: 'Bound' },
          { value: 'active', label: 'Active' },
          { value: 'expired', label: 'Expired' },
          { value: 'run_off', label: 'Run-off' },
        ] },
      ]}
      columns={columns}
      emptyMessage={t('screens.reTreaties.empty', 'No treaties yet. Create one to start building the register.')}
      drawerTabs={['Overview', 'Layers', 'Participants', 'Documents']}
      drawerHeader={(r) => ({
        title: r.treatyNumber,
        badge: <Pill status={r.status} />,
        lines: [
          r.cedant?.name,
          [r.kind === 'facultative' ? 'Facultative' : 'Treaty', r.classOfBusiness].filter(Boolean).join(' · '),
        ].filter(Boolean),
      })}
      drawer={(r, { tab, reload }) => (
        <TreatyDrawer row={r} tab={tab} reload={reload} />
      )}
    />
  );
}
